/**
 * m3 echo Frontend - WebSocket Service
 *
 * Manages WebSocket connection for real-time job progress updates.
 *
 * Security improvements (v4.7.0):
 * - Token now sourced from auth store (in-memory) instead of localStorage
 * - Supports token injection via connect() parameter
 * - Auto-reconnect respects token availability
 * - A session the server has revoked ends the connection for good (4401)
 */

import { useAuthStore } from '../stores/useAuthStore';
import { getWebSocketUrl } from './apiBaseUrl';

/**
 * Application close code the server uses when it drops a connection whose
 * session is no longer valid - logout, deactivation, tenant suspension.
 *
 * Must stay in sync with WS_CLOSE_SESSION_REVOKED in
 * backend/app/api/websocket.py. 4401 mirrors HTTP 401 inside the range the
 * WebSocket spec reserves for applications (4000-4999).
 *
 * It is the one close reason reconnecting cannot fix: the handshake would be
 * refused with the same token. Retrying it ten times only delays the moment the
 * user learns their session is gone.
 */
const WS_CLOSE_SESSION_REVOKED = 4401;

/**
 * WebSocket message types from server
 */
export interface JobProgressMessage {
  type: 'job_progress' | 'job_completed';
  job_id: number;
  status: 'running' | 'completed' | 'failed';
  current: number;
  total: number;
  message: string;
  timestamp: string;
}

export interface SubscribedMessage {
  type: 'subscribed';
  job_id: number;
}

export interface UnsubscribedMessage {
  type: 'unsubscribed';
  job_id: number;
}

export interface ErrorMessage {
  type: 'error';
  message: string;
}

export interface AuthenticatedMessage {
  type: 'authenticated';
  user_id: number;
  email: string;
}

export type WebSocketMessage = JobProgressMessage | SubscribedMessage | UnsubscribedMessage | ErrorMessage | AuthenticatedMessage;

/**
 * Callback function types
 */
export type ProgressCallback = (message: JobProgressMessage) => void;
export type ErrorCallback = (error: string) => void;
export type ConnectionCallback = () => void;

/**
 * WebSocket Service for real-time job progress updates
 */
class WebSocketService {
  private ws: WebSocket | null = null;
  private url: string;
  private reconnectTimeout: number = 3000;
  private reconnectAttempts: number = 0;
  private maxReconnectAttempts: number = 10;
  private subscribedJobs: Set<number> = new Set();
  private currentToken: string | null = null;  // Current token for reconnection

  // Callbacks
  private progressCallbacks: Map<number, ProgressCallback[]> = new Map();
  private errorCallback: ErrorCallback | null = null;
  private connectCallback: ConnectionCallback | null = null;
  private disconnectCallback: ConnectionCallback | null = null;

  constructor() {
    this.url = getWebSocketUrl();
    console.log(`[WebSocket] Will connect to: ${this.url}`);
  }

  /**
   * Connect to WebSocket server with JWT authentication.
   *
   * @param providedToken - Optional token to use (for explicit injection).
   *                        If not provided, retrieves from auth store.
   */
  public connect(providedToken?: string): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      console.log('[WebSocket] Already connected');
      return;
    }

    // Get JWT token from auth store (in-memory) or use provided token
    let token: string | null = providedToken || null;

    if (!token) {
      // Get token from Zustand auth store (in-memory, not localStorage)
      const authState = useAuthStore.getState();
      token = authState.accessToken;
    }

    if (!token) {
      console.error('[WebSocket] No authentication token available');
      if (this.errorCallback) {
        this.errorCallback('Authentication required for WebSocket connection');
      }
      return;
    }

    // Store token for reconnect attempts
    this.currentToken = token;

    // Connect without token in URL (secure!)
    console.log(`[WebSocket] Connecting to ${this.url}...`);

    try {
      this.ws = new WebSocket(this.url);

      this.ws.onopen = () => {
        console.log('[WebSocket] Connection established, authenticating...');

        // Send authentication message immediately after connection
        this.sendMessage({
          action: 'auth',
          token: this.currentToken
        });
      };

      this.ws.onmessage = (event) => {
        try {
          const message: WebSocketMessage = JSON.parse(event.data);
          this.handleMessage(message);
        } catch (error) {
          console.error('[WebSocket] Failed to parse message:', error);
          if (this.errorCallback) {
            this.errorCallback(`Failed to parse WebSocket message: ${error instanceof Error ? error.message : 'Unknown error'}`);
          }
        }
      };

      this.ws.onerror = (error) => {
        console.error('[WebSocket] Error:', error);
        if (this.errorCallback) {
          this.errorCallback('WebSocket connection error');
        }
      };

      this.ws.onclose = (event) => {
        console.log(`[WebSocket] Connection closed (code ${event.code})`);

        if (this.disconnectCallback) {
          this.disconnectCallback();
        }

        // A revoked session is final - reconnecting would just be refused at
        // the handshake, once per attempt.
        if (event.code === WS_CLOSE_SESSION_REVOKED) {
          this.handleSessionRevoked();
          return;
        }

        // Attempt to reconnect
        this.attemptReconnect();
      };

    } catch (error) {
      console.error('[WebSocket] Failed to create connection:', error);
      this.attemptReconnect();
    }
  }

  /**
   * Disconnect from WebSocket server
   */
  public disconnect(): void {
    if (this.ws) {
      console.log('[WebSocket] Disconnecting...');
      this.ws.close();
      this.ws = null;
    }
    this.subscribedJobs.clear();
  }

  /**
   * Subscribe to job progress updates
   */
  public subscribeToJob(jobId: number, callback: ProgressCallback): void {
    // Add to callbacks
    if (!this.progressCallbacks.has(jobId)) {
      this.progressCallbacks.set(jobId, []);
    }
    this.progressCallbacks.get(jobId)!.push(callback);

    // Add to subscribed jobs
    this.subscribedJobs.add(jobId);

    // Send subscription message
    if (this.isConnected()) {
      this.sendMessage({
        action: 'subscribe',
        job_id: jobId
      });
    }
  }

  /**
   * Unsubscribe from job progress updates
   */
  public unsubscribeFromJob(jobId: number, callback?: ProgressCallback): void {
    if (callback) {
      // Remove specific callback
      const callbacks = this.progressCallbacks.get(jobId);
      if (callbacks) {
        const filtered = callbacks.filter(cb => cb !== callback);
        if (filtered.length > 0) {
          this.progressCallbacks.set(jobId, filtered);
        } else {
          this.progressCallbacks.delete(jobId);
        }
      }
    } else {
      // Remove all callbacks for this job
      this.progressCallbacks.delete(jobId);
    }

    // If no more callbacks for this job, unsubscribe
    if (!this.progressCallbacks.has(jobId)) {
      this.subscribedJobs.delete(jobId);

      if (this.isConnected()) {
        this.sendMessage({
          action: 'unsubscribe',
          job_id: jobId
        });
      }
    }
  }

  /**
   * Set error callback
   */
  public onError(callback: ErrorCallback): void {
    this.errorCallback = callback;
  }

  /**
   * Set connection callback
   */
  public onConnect(callback: ConnectionCallback): void {
    this.connectCallback = callback;
  }

  /**
   * Set disconnection callback
   */
  public onDisconnect(callback: ConnectionCallback): void {
    this.disconnectCallback = callback;
  }

  /**
   * Check if WebSocket is connected
   */
  public isConnected(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }

  /**
   * Send message to WebSocket server
   */
  private sendMessage(message: any): void {
    if (!this.isConnected()) {
      console.error('[WebSocket] Cannot send message - not connected');
      return;
    }

    try {
      this.ws!.send(JSON.stringify(message));
    } catch (error) {
      console.error('[WebSocket] Failed to send message:', error);
    }
  }

  /**
   * Handle incoming WebSocket message
   */
  private handleMessage(message: WebSocketMessage): void {
    switch (message.type) {
      case 'authenticated':
        console.log(`[WebSocket] Authenticated as ${message.email}`);
        this.reconnectAttempts = 0;

        // Re-subscribe to all jobs that were subscribed before disconnect
        this.subscribedJobs.forEach(jobId => {
          this.sendMessage({
            action: 'subscribe',
            job_id: jobId
          });
        });

        if (this.connectCallback) {
          this.connectCallback();
        }
        break;

      case 'job_progress':
      case 'job_completed':
        this.handleProgressMessage(message as JobProgressMessage);
        break;

      case 'subscribed':
        console.log(`[WebSocket] Subscribed to job ${message.job_id}`);
        break;

      case 'unsubscribed':
        console.log(`[WebSocket] Unsubscribed from job ${message.job_id}`);
        break;

      case 'error':
        console.error('[WebSocket] Server error:', message.message);
        if (this.errorCallback) {
          this.errorCallback(message.message);
        }
        break;

      default:
        console.warn('[WebSocket] Unknown message type:', message);
    }
  }

  /**
   * Handle job progress message
   */
  private handleProgressMessage(message: JobProgressMessage): void {
    const callbacks = this.progressCallbacks.get(message.job_id);

    if (callbacks && callbacks.length > 0) {
      callbacks.forEach(callback => {
        try {
          callback(message);
        } catch (error) {
          console.error('[WebSocket] Error in progress callback:', error);
        }
      });
    }
  }

  /**
   * Handle a connection the server closed because the session is gone.
   *
   * Treated exactly like a failed token refresh in services/api.ts: the local
   * auth state is cleared and the user is sent to the login page. The socket
   * and the HTTP client authenticate against the same session record, so a
   * session the socket may no longer use is one the API will refuse as well -
   * staying on the page would only produce 401s.
   *
   * The `isAuthenticated` guard covers the ordinary logout race, where the
   * server drops the socket a moment before or after the client tears it down
   * itself. Auth state is already cleared then, and there is nothing to
   * announce and nowhere to redirect to. The pathname check keeps a redirect
   * from firing while already on /login.
   */
  private handleSessionRevoked(): void {
    console.warn('[WebSocket] Session revoked by server, not reconnecting');

    this.subscribedJobs.clear();
    this.currentToken = null;
    this.reconnectAttempts = 0;

    const authState = useAuthStore.getState();
    if (!authState.isAuthenticated) {
      return;
    }

    if (this.errorCallback) {
      this.errorCallback('Sitzung abgelaufen - bitte erneut anmelden');
    }

    authState.clearAuth();

    if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
  }

  /**
   * Attempt to reconnect to WebSocket server
   */
  private attemptReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('[WebSocket] Max reconnect attempts reached');
      if (this.errorCallback) {
        this.errorCallback('Failed to reconnect to WebSocket server');
      }
      return;
    }

    // Check if user is still authenticated before reconnecting
    const authState = useAuthStore.getState();
    if (!authState.isAuthenticated || !authState.accessToken) {
      console.log('[WebSocket] User not authenticated, skipping reconnect');
      this.reconnectAttempts = 0;  // Reset for next login
      return;
    }

    // Use fresh token from auth store
    this.currentToken = authState.accessToken;

    this.reconnectAttempts++;
    const timeout = this.reconnectTimeout * Math.min(this.reconnectAttempts, 5);

    console.log(`[WebSocket] Reconnecting in ${timeout}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`);

    setTimeout(() => {
      this.connect();
    }, timeout);
  }

  /**
   * Update the authentication token (e.g., after token refresh).
   * If connected, will close and reconnect with new token.
   *
   * @param token - New access token
   */
  public updateToken(token: string): void {
    this.currentToken = token;

    // If currently connected, reconnect with new token
    if (this.isConnected()) {
      console.log('[WebSocket] Token updated, reconnecting...');
      this.reconnectAttempts = 0;  // Reset reconnect counter
      this.ws?.close();
      // Reconnect will happen via onclose handler
    }
  }

  /**
   * Reset connection state (e.g., on logout).
   * Disconnects and clears all state.
   */
  public reset(): void {
    this.disconnect();
    this.currentToken = null;
    this.reconnectAttempts = 0;
    this.progressCallbacks.clear();
  }
}

// Export singleton instance
export const websocketService = new WebSocketService();
export default websocketService;
